import React, { useState, useEffect, useContext } from 'react';
import SinglePageForm from '../common/SinglePageForm';
import { Alert, Form } from 'antd';
import { confirmEmail } from '../services/user';
import { resendVerificationEmail } from '../services/userProfile';
import { withRouter, NavLink, useHistory } from 'react-router-dom';
import { parse as parseQueryString } from 'query-string';
import Loader from '../common/Loader';
import { CustomAppContext } from '../common/CustomAppContext';
import './VerifyAccountPage.less';
import { APP_CCX } from '../common/CustomAppContext';
import { trackCustomEvent } from '../helpers';

export default withRouter(VerifyAccountPage);

/**
 * @name VerifyAccountPage
 * @desc component.
 */
function VerifyAccountPage({ location }) {
    const [form] = Form.useForm();
    const [error, setError] = useState(null);
    const [verified, setVerified] = useState(null);
    const [sent, setSent] = useState(null);
    const [loading, setLoading] = useState(false);
    const {
        currentApp,
        getCustomAppPath,
        getCustomAppButtonStyle,
        getVerificationEmailPath,
    } = useContext(CustomAppContext);
    const history = useHistory();

    useEffect(() => {
        (async () => {
            const { t, uid, initial, send } = parseQueryString(location.search);
            if (location.state?.email) {
                setSent(false);
                setVerified(false);
            } else {
                if (initial) {
                    setSent(false);
                    setVerified(false);

                    if (send) {
                        handleSendEmailVerificationClick();
                    }
                } else {
                    try {
                        if (!t || !uid) {
                            throw new Error('Missing params');
                        }
                        await confirmEmail({
                            id: uid,
                            token: t,
                        });
                        setVerified(true);
                        history.push(getCustomAppPath('/'));
                    } catch (err) {
                        setVerified(false);
                        setError(
                            `An error has occurred and your account has been not verified: ${err.message}`,
                        );
                    }
                }
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSendEmailVerificationClick = async () => {
        setLoading(true);
        try {
            await resendVerificationEmail(getVerificationEmailPath());
            setSent(true);
            setError(null);
            trackCustomEvent(
                currentApp,
                'user_verification_email_success',
                'click',
            );
        } catch (err) {
            setSent(false);
            setError(err.message);
            trackCustomEvent(
                currentApp,
                'user_verification_email_failed',
                'click',
            );
        }
        setLoading(false);
    };

    return (
        <SinglePageForm form={form} data-testid="verify-account-form">
            <SinglePageForm.Content>
                <h1 className="VerifyAccountPage-form-title">
                    Confirm your email
                </h1>
                <Form.Item>
                    {error ? (
                        <Alert
                            data-testid="verify-account-error"
                            message={error}
                            type="error"
                        />
                    ) : null}
                    {verified === null ? (
                        <Loader data-testid="verify-account-loader" full />
                    ) : null}
                    {verified === true ? (
                        <>
                            <Alert
                                data-testid="verify-account-success"
                                message={`Your account has been successfully verified!`}
                                type="success"
                            />
                            <NavLink
                                to={getCustomAppPath('/')}
                                className={`VerifyAccountPage-link-${currentApp}`}
                            >
                                Log In
                            </NavLink>
                        </>
                    ) : null}

                    {verified === false ? (
                        <span className="VerifyAccountPage-resend">
                            {location?.state?.email && (
                                <p>
                                    We've send a confirmational email to this
                                    address -
                                    <strong>{location?.state?.email}</strong>.
                                    Please, follow the instructions and confirm
                                    your email.
                                </p>
                            )}

                            <p>
                                If you don't receive the email, please, check
                                your spam folder or
                                <button
                                    data-testid="verify-account-resend-button"
                                    onClick={handleSendEmailVerificationClick}
                                >
                                    resend the email
                                </button>
                                again.
                            </p>
                        </span>
                    ) : null}
                    {sent ? (
                        <Alert
                            message="Email resent successfully"
                            type="success"
                            showIcon
                        />
                    ) : null}
                </Form.Item>
            </SinglePageForm.Content>
        </SinglePageForm>
    );
}
